import { z } from "zod";
import type { Operation } from "../types";

/**
 * Context Inspector contracts (Data Design §5.6). Two operations:
 *   - `build` (POST) — assemble the ranked, cited, token-budgeted context pack
 *     for a subject (task / goal / entity urn / free question). The `route.ts`
 *     handler calls `buildContextPack` from `@excellent/core/server`; the client
 *     `/agents/context` page calls it via `apiCall`.
 *   - `ftsStatus` (GET) / `ftsRebuild` (POST) — the schema FTS retrieval
 *     status + rebuild, driving the `/settings/agents/context` retrieval section.
 *
 * The pack response is a `z.custom` over the core `ContextPack` shape (mirrored
 * as a plain interface here so the UI needn't import a server-only type). The
 * route validates by returning the real pack; the client trusts the envelope.
 */

/** One assembled, ranked, cited context item — mirror of core `ContextItem`. */
export interface ContextInspectorItem {
  source: string;
  urn: string;
  title: string;
  snippet: string | null;
  occurred_at: string | null;
  score: number;
  tokens: number;
  citation: { urn: string; source: string; label: string; snippet?: string | null };
}

export interface ContextInspectorPack {
  items: ContextInspectorItem[];
  tokenEstimate: number;
  assembledFrom: Record<string, number>;
  correlationUid: string;
  /** Wall-clock assembly time in ms (measured by the route). */
  assemblyMs: number;
}

const buildBody = z.object({
  task: z.number().int().positive().optional(),
  goal: z.number().int().positive().optional(),
  urn: z.string().optional(),
  question: z.string().optional(),
  tokenBudget: z.number().int().positive().max(32000).optional(),
});
export type ContextPackBuildBody = z.infer<typeof buildBody>;

const buildOp: Operation<void, void, ContextPackBuildBody, ContextInspectorPack> = {
  method: "POST",
  path: () => "/agents/api/context-pack",
  body: buildBody,
  response: z.custom<ContextInspectorPack>(),
};

export interface FtsStatus {
  /** Total indexed docs across kinds. */
  indexed: number;
  /** Per-kind doc counts. */
  byKind: Record<string, number>;
  /** Per-source relevance weights (persisted; surfaced for the settings page). */
  weights: Record<string, number>;
  /** Default token budget + per-source caps (persisted). */
  tokenBudget: number;
  perSourceCaps: Record<string, number>;
}

const ftsStatusOp: Operation<void, void, void, FtsStatus> = {
  method: "GET",
  path: () => "/agents/api/context-pack/fts",
  response: z.custom<FtsStatus>(),
};

// coverage-ignore: ftsRebuild Hono port deferred; ftsStatus/build/saveConfig mounted
const ftsRebuildOp: Operation<void, void, void, { indexed: number; byKind: Record<string, number> }> = {
  method: "POST",
  path: () => "/agents/api/context-pack/fts",
  response: z.custom<{ indexed: number; byKind: Record<string, number> }>(),
};

/** Persist retrieval config (per-source weights, token budget, per-source caps). */
const configBody = z.object({
  weights: z.record(z.string(), z.number()).optional(),
  tokenBudget: z.number().int().positive().max(32000).optional(),
  perSourceCaps: z.record(z.string(), z.number()).optional(),
});
export type ContextConfigBody = z.infer<typeof configBody>;

const saveConfigOp: Operation<void, void, ContextConfigBody, { ok: true }> = {
  method: "POST",
  path: () => "/agents/api/context-pack/config",
  body: configBody,
  response: z.custom<{ ok: true }>(),
};

export const contextPack = {
  build: buildOp,
  ftsStatus: ftsStatusOp,
  ftsRebuild: ftsRebuildOp,
  saveConfig: saveConfigOp,
};
